import React, { useMemo, useState } from 'react'
import { Card, Collapse, Tag, Empty, Typography, Radio, Space } from 'antd'
import {
  HistoryOutlined,
  BugOutlined,
  StarOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import {
  ANNOUNCEMENTS,
  ANNOUNCEMENT_PLATFORMS,
  platformOf
} from '../../data/announcements'

const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse

const PageContainer = styled.div`
  padding: 24px;
  background: transparent;
  min-height: calc(100vh - 64px);
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  h1 {
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
  }
`

const SubHint = styled.div`
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 20px;
`

const FilterBar = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`

const VersionCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
  border: none;
`

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .version-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .version-date {
    color: #6b7280;
    font-size: 14px;
  }

  .version-counts {
    margin-left: auto;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
`

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-weight: 500;
  color: #1a1a1a;
`

const HowToUseBlock = styled.div`
  background: #f9fafb;
  border-left: 3px solid #7c3aed;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 12px;

  .how-to-heading {
    font-weight: 600;
    color: #4c1d95;
    margin-bottom: 4px;
    font-size: 13px;
    letter-spacing: 0.5px;
  }
`

const StepGroup = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .group-heading {
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  ol {
    margin: 0;
    padding-left: 20px;
    color: #374151;

    li {
      margin-bottom: 4px;
      line-height: 1.7;
    }
  }
`

const KIND_META = {
  feature: { label: '新功能', color: 'purple', icon: <StarOutlined /> },
  bugfix: { label: 'Bug 修復', color: 'red', icon: <BugOutlined /> },
  improvement: {
    label: '體驗改善',
    color: 'cyan',
    icon: <ThunderboltOutlined />
  }
}

const renderHowToUse = (section) => {
  if (section.howToUseSteps && Array.isArray(section.howToUseSteps)) {
    return (
      <HowToUseBlock>
        <div className='how-to-heading'>店家怎麼用</div>
        {section.howToUseSteps.map((group, idx) => (
          <StepGroup key={idx}>
            {group.heading && (
              <div className='group-heading'>{group.heading}</div>
            )}
            <ol>
              {group.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </StepGroup>
        ))}
      </HowToUseBlock>
    )
  }
  if (section.howToUse) {
    return (
      <HowToUseBlock>
        <div className='how-to-heading'>店家怎麼用</div>
        <Text>{section.howToUse}</Text>
      </HowToUseBlock>
    )
  }
  return null
}

const countByKind = (sections) => {
  const counts = { feature: 0, improvement: 0, bugfix: 0 }
  for (const s of sections) {
    if (counts[s.kind] !== undefined) counts[s.kind]++
  }
  return counts
}

const ChangelogPage = () => {
  // 篩選器：all / feature / improvement / bugfix；admin 預設 all（含 bug 修復）
  const [kindFilter, setKindFilter] = useState('all')

  const filtered = useMemo(() => {
    return ANNOUNCEMENTS.map((ann) => ({
      ...ann,
      sections:
        kindFilter === 'all'
          ? ann.sections
          : ann.sections.filter((s) => s.kind === kindFilter)
    })).filter((ann) => ann.sections.length > 0)
  }, [kindFilter])

  if (!ANNOUNCEMENTS || ANNOUNCEMENTS.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <HistoryOutlined style={{ fontSize: 24, color: '#7c3aed' }} />
          <h1>系統更新紀錄</h1>
        </PageHeader>
        <Empty description='目前沒有更新紀錄' />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <HistoryOutlined style={{ fontSize: 24, color: '#7c3aed' }} />
        <h1>系統更新紀錄</h1>
      </PageHeader>
      <SubHint>
        Admin 端完整版更新紀錄，包含店家側不會公開的 bug 修正。資料來源與店家公告頁同一份檔案（`src/data/announcements.js`），有更新只要在那邊新增一筆，並更新「該平台」的最新版本字串（`LATEST_VERSION` / `LATEST_VERSION_APP`）即可。這一頁不分平台，全部依時間排在一起，平台用標籤區分。
      </SubHint>

      <FilterBar>
        <Text strong>篩選類型：</Text>
        <Radio.Group
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          optionType='button'
          buttonStyle='solid'
        >
          <Radio.Button value='all'>全部</Radio.Button>
          <Radio.Button value='feature'>新功能</Radio.Button>
          <Radio.Button value='improvement'>體驗改善</Radio.Button>
          <Radio.Button value='bugfix'>Bug 修復</Radio.Button>
        </Radio.Group>
      </FilterBar>

      {filtered.length === 0 ? (
        <Empty description='目前篩選類型下沒有紀錄' />
      ) : (
        filtered.map((ann) => {
          const counts = countByKind(ann.sections)
          return (
            <VersionCard
              key={ann.version}
              title={
                <VersionHeader>
                  <span className='version-title'>{ann.title}</span>
                  <span className='version-date'>{ann.version}</span>
                  <Tag color={platformOf(ann) === 'app' ? 'green' : 'blue'}>
                    {
                      (
                        ANNOUNCEMENT_PLATFORMS.find(
                          (p) => p.key === platformOf(ann)
                        ) || {}
                      ).label
                    }
                  </Tag>
                  <Space className='version-counts'>
                    {counts.feature > 0 && (
                      <Tag color='purple'>新功能 {counts.feature}</Tag>
                    )}
                    {counts.improvement > 0 && (
                      <Tag color='cyan'>體驗改善 {counts.improvement}</Tag>
                    )}
                    {counts.bugfix > 0 && (
                      <Tag color='red'>Bug 修復 {counts.bugfix}</Tag>
                    )}
                  </Space>
                </VersionHeader>
              }
            >
              {ann.summary && (
                <Paragraph style={{ color: '#4b5563' }}>
                  {ann.summary}
                </Paragraph>
              )}

              <Collapse
                defaultActiveKey={ann.sections.map(
                  (_, i) => `${ann.version}-${i}`
                )}
                ghost
              >
                {ann.sections.map((section, i) => {
                  const meta = KIND_META[section.kind] || KIND_META.improvement
                  return (
                    <Panel
                      key={`${ann.version}-${i}`}
                      header={
                        <SectionTitleRow>
                          <Tag color={meta.color} icon={meta.icon}>
                            {meta.label}
                          </Tag>
                          <span>{section.title}</span>
                          {section.shopHidden && (
                            <Tag color='default'>店家側不顯示</Tag>
                          )}
                        </SectionTitleRow>
                      }
                    >
                      {section.description && (
                        <Paragraph style={{ marginBottom: 8 }}>
                          {section.description}
                        </Paragraph>
                      )}
                      {/* admin 端額外顯示工程備註（只有 admin 會看到） */}
                      {section.devNote && (
                        <Paragraph
                          style={{
                            background: '#fff7ed',
                            padding: 8,
                            borderRadius: 4,
                            color: '#7c2d12',
                            fontSize: 13,
                            marginBottom: 8
                          }}
                        >
                          <Text strong>工程備註：</Text>
                          {section.devNote}
                        </Paragraph>
                      )}
                      {renderHowToUse(section)}
                    </Panel>
                  )
                })}
              </Collapse>
            </VersionCard>
          )
        })
      )}
    </PageContainer>
  )
}

export default ChangelogPage
